import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { Text, Title, Paper, Tabs, Grid, GridCol, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { DonutCard, BarChartComp, DonutCardWithTabs } from './components/charts';
import { MachineTable, OperationList } from './components/tables';
import { daily } from './mock-data';
import { useEffect, useRef, useState } from 'react';
import { fetchTransactionList, OrderBy, Transaction } from '../../../libraries/transactions';
import { fetchEquipmentList, IEquipmentDetails } from '../../../libraries/equipment';
import { LoginState } from '../../client-redux/login/reducer';
import { ILocation } from '../../../libraries/location';
import { daysOrMinutesAgo, delegationCheck, formatCurrAmount } from '../../utils/script';
import { ICurrency } from '../../../libraries/currencies';
import MapMarkers, { MapPin } from './components/map-markers';
import { toMoment } from '../../utils/date';
import { DayliTotals, fetchDailyTotalsDays } from '../../../libraries/daily-totals';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { IconDatabaseOff, IconClock, IconCircleFilled } from '@tabler/icons-react';
import './styles.scss';

function DashboardEntity() {
  const getTransController = useRef(new AbortController()).current;
  const getEquiController = useRef(new AbortController()).current;
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const [lastTxn, setLastTxn] = useState<Date | string>();
  const [countOrange, setCountOrange] = useState(0);
  const [countRed, setCountRed] = useState(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [mapPins, setMapPins] = useState<MapPin[]>([]);
  const [equipmentList, setEquipmentList] = useState<IEquipmentDetails[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [days, setDays] = useState<number>(7);
  const [dailyTotals, setDailyTotals] = useState<DayliTotals[]>([]);
  const [activeCid, setActiveCid] = useState<string | null>(null);
  const [uniqueCids, setUniqueCids] = useState<number[]>([]);
  const getCurrencyByCid = (cid: number) => currencies?.find(c => c.cid === cid);

  const almostFullList = equipmentList?.filter(m => (m?.safeOccPerc || 0) >= 80 && (m?.safeOccPerc || 0) < 95);
  const fullList = equipmentList?.filter(m => (m?.safeOccPerc || 0) >= 95);
  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (!dailyTotals || dailyTotals.length === 0) return;
    const cids = Array.from(new Set(dailyTotals.flatMap(day => day.totals.map(t => t.cid))));
    setUniqueCids(cids);
    if (!activeCid && cids.length > 0) {
      setActiveCid(String(cids[0]));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyTotals]);

  useEffect(() => {
    if (!currencies || currencies.length === 0) return;
    getTransactionDetails();
    let curr = currencies?.find(c => c.cid === 1);
    daily.forEach(d => {
      d.barChartPoint = d.amount / 100;
      d.amountFormatted = formatCurrAmount(d.amount, curr);
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencies]);

  useEffect(() => {
    if (!locations || locations.length === 0) return;
    getEquipmentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const getTransactionDetails = () => {
    setDays(7);
    setTransactionsLoading(true);

    const dailyController = new AbortController();
    Promise.all([
      fetchTransactionList(
        getTransController,
        0,
        0,
        0,
        0,
        0,
        10,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        OrderBy.DATE_DESC
      ),
      fetchDailyTotalsDays(dailyController, login.user.entid, days)
    ])
      .then(([txnResponse, dailyResponse]) => {
        if (!isZarphSuccessResponse(txnResponse)) {
          handleApiError(txnResponse, lang.ALERT_ERROR_FETCHING_DATA);
          setTransactions([]);
        } else {
          setTransactions(txnResponse?.data?.transactions || []);
        }

        // TODO: handle daily totals response errors; change fetchDailyTotalsDays to return AxiosResponse and check isZarphSuccessResponse before setting state
        setDailyTotals(Array.isArray(dailyResponse) ? dailyResponse : []);
      })
      .catch(err => {
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => setTransactionsLoading(false));

    return () => dailyController.abort();
  };

  const getEquipmentList = () => {
    setEquipmentLoading(true);

    fetchEquipmentList(getEquiController, login.user.entid, 0, 0, 0, false)
      .then(response => {
        if (!isZarphSuccessResponse(response)) {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setEquipmentList([]);
          setMapPins([]);
          return;
        }
        const equipments = response.data.equipments || [];

        setCountOrange(
          equipments.filter((m: IEquipmentDetails) => (m.safeOccPerc || 0) >= 80 && (m.safeOccPerc || 0) < 95).length
        );

        setCountRed(equipments.filter((m: IEquipmentDetails) => (m.safeOccPerc || 0) >= 95).length);

        const matchingCurrency = currencies?.find(c => c.cid === 1);
        let lastDate: string | undefined;

        const pins: MapPin[] = [];

        equipments.forEach((equi: IEquipmentDetails) => {
          equi.location = delegationCheck(equi.equipment.sid, locations);

          const sid = equi.equipment.sid;
          const location = locations.find(loc => loc.sid === sid);
          if (!location) return;

          equi.amount = formatCurrAmount(equi?.periodAmounts?.find(p => p.cid === 1)?.total || 0, matchingCurrency);

          equi.position = getPositionCoordinates(equipments, equi.equipment.eid, sid);

          const equiDate = toMoment(equi.lastOperationDt);
          if (equiDate.isValid() && (!lastDate || equiDate.isAfter(toMoment(lastDate)))) {
            lastDate = equiDate.toISOString();
          }

          let pin = pins.find(p => p.location.sid === sid);
          if (!pin) {
            pin = {
              location,
              equipments: [],
              position: { lat: location.lat || 0, lng: location.lng || 0 }
            };
            pins.push(pin);
          }

          pin.equipments.push(equi);
        });

        setLastTxn(lastDate ?? undefined);
        setEquipmentList(equipments);
        setMapPins(pins.filter(p => p.equipments.length > 0));
      })
      .catch(err => {
        handleApiError(err, lang.GLOBAL_ERROR);
      })
      .finally(() => setEquipmentLoading(false));
  };

  const getPositionCoordinates = (
    equipmentList: IEquipmentDetails[],
    eid: number,
    sid: number
  ): { lat: number; lng: number } => {
    let nEquiInLocation = equipmentList.reduce((acc, equi) => {
      if (equi.equipment.sid === sid) {
        acc++;
      }
      return acc;
    }, 0);
    let offset = nEquiInLocation > 1 ? (eid / 1000) * 0.001 : 0;
    const location = locations.find(loc => loc.sid === sid);
    return { lat: location?.lat ? location?.lat + offset : 0, lng: location?.lng || 0 };
  };

  const Triples: React.FC = () => (
    <div className="triples">
      <Paper className="info-box">
        <IconClock stroke={2} />
        <Title>{lang.DASHBOARD_LAST_TRANSACTION}</Title>
        <Text>{lastTxn ? daysOrMinutesAgo(lastTxn, lang) : '-'}</Text>
      </Paper>

      <Paper className="info-box">
        <IconCircleFilled className="yellow-warning" />
        <Title>{lang.DASHBOARD_ALMOST_FULL}</Title>
        <Text>{countOrange}</Text>
      </Paper>

      <Paper className="info-box">
        <IconCircleFilled className="red-warning" />
        <Title>{lang.DASHBOARD_FULL}</Title>
        <Text>{countRed}</Text>
      </Paper>
    </div>
  );

  return (
    <div className="dashboard-entity">
      <Header pageTitle={lang.DASHBORAD_ENTITY} />

      <div className="charts-wrapper">
        {/* Triples component */}
        {Array.isArray(dailyTotals) && dailyTotals.length === 0 && <Triples />}

        {Array.isArray(dailyTotals) && dailyTotals.length > 0 ? (
          // Data exists → render tabs
          <Tabs
            value={activeCid ?? undefined}
            onChange={val => val && setActiveCid(val)}
            keepMounted={false}
            radius="sm"
            className="main-tabs"
          >
            <Tabs.List>
              {uniqueCids.map(cid => {
                const currencyForCid = getCurrencyByCid(cid);
                return (
                  <Tabs.Tab key={cid} value={String(cid)}>
                    {currencyForCid?.currencyStr ?? `CID ${cid}`}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>

            {uniqueCids.map(cid => {
              const currencyForCid = getCurrencyByCid(cid);
              const dailyForCid = dailyTotals.map(day => ({
                ...day,
                totals: day.totals.filter(t => t.cid === cid)
              }));

              return (
                <Tabs.Panel key={cid} value={String(cid)}>
                  <Grid gap="md">
                    {/* Left side: Info boxes + Donuts */}
                    <GridCol span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                      <Group className="charts-row">
                        <GridCol span={12}>
                          <Triples />
                        </GridCol>

                        <Group className="donuts">
                          <GridCol span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                            <DonutCard
                              label={lang.DASHBOARD_FULL_AMOUNT}
                              data={dailyTotals}
                              currency={currencyForCid}
                              cid={cid}
                              maxValue={50000}
                              mode="today"
                            />
                          </GridCol>

                          <GridCol span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                            <DonutCardWithTabs activeCid={cid} currencies={currencies} />
                          </GridCol>
                        </Group>
                      </Group>
                    </GridCol>

                    <GridCol span={{ base: 12, sm: 12, md: 6, lg: 6 }}>
                      {/* Right side: Bar chart */}
                      <BarChartComp data={dailyForCid} currency={currencyForCid} />
                    </GridCol>
                  </Grid>
                </Tabs.Panel>
              );
            })}
          </Tabs>
        ) : (
          // No data → show no-data placeholder
          <Paper
            withBorder
            shadow="sm"
            radius="md"
            p="xl"
            className="no-data"
            style={{ textAlign: 'center', marginTop: '2rem', color: '#777' }}
          >
            <IconDatabaseOff size={40} stroke={1.5} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <Title order={4}>{lang.EQUIPMENT_NO_DATA}</Title>
          </Paper>
        )}
      </div>

      {/* Map */}
      <div className="map-markers-container">
        <MapMarkers pins={mapPins} />
      </div>

      {/* Machine tables */}
      <div className="machine-tables-container">
        <MachineTable
          list={almostFullList}
          title={lang.DASHBOARD_ALMOST_FULL}
          loading={equipmentLoading}
          noResults={!equipmentLoading && !almostFullList.length}
        />

        <MachineTable
          list={fullList}
          title={lang.DASHBOARD_FULL}
          loading={equipmentLoading}
          noResults={!equipmentLoading && !fullList.length}
        />
      </div>

      {/* Operations */}
      <div className="operation-list-container">
        <OperationList
          operations={transactions}
          loading={transactionsLoading}
          noResults={!transactionsLoading && !transactions.length}
        />
      </div>
    </div>
  );
}

export default DashboardEntity;
